import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const { isAuthenticated } = useAuth();

    const fetchCart = useCallback(async () => {
        setLoading(true);
        try {
            if (isAuthenticated) {
                // Untuk user login, data diambil dari API
                const { data } = await api.get('/cart');
                const validItems = data.CartItems ? data.CartItems.filter(item => item.Product) : [];
                setCartItems(validItems);
            } else {
                // Untuk GUEST, kita juga perlu mengambil data produk terbaru
                const localCart = JSON.parse(localStorage.getItem('cart')) || [];
                if (localCart.length > 0) {
                    const productIds = localCart.map(item => item.ProductId);
                    const { data: detailedProducts } = await api.post('/products/details-for-cart', { productIds });

                    // Gabungkan detail dari server dengan kuantitas dari local storage
                    const hydratedCart = localCart.map(localItem => {
                        const productDetail = detailedProducts.find(p => p.id === localItem.ProductId);
                        return {
                            ...localItem,
                            Product: productDetail
                        };
                    }).filter(item => item.Product); // Filter item yang produknya mungkin sudah dihapus
                    setCartItems(hydratedCart);
                } else {
                    setCartItems([]);
                }
            }
        } catch (error) {
            console.error("Gagal memuat keranjang:", error);
            setCartItems([]);
            if (!isAuthenticated) {
                localStorage.removeItem('cart');
            }
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    const addToCart = async (productId, quantity) => {
        try {
            if (isAuthenticated) {
                await api.post('/cart', { productId, quantity });
            } else {
                let localCart = JSON.parse(localStorage.getItem('cart')) || [];
                const existingItemIndex = localCart.findIndex(item => item.ProductId === productId);
                
                if (existingItemIndex > -1) {
                    localCart[existingItemIndex].quantity += quantity;
                } else {
                    localCart.push({ 
                        ProductId: productId, 
                        quantity,
                    });
                }
                localStorage.setItem('cart', JSON.stringify(localCart));
            }
            await fetchCart();
        } catch (error) {
            console.error("Gagal menambahkan ke keranjang:", error);
            throw error;
        }
    };

    const updateQuantity = async (productId, quantity) => {
        try {
            if (isAuthenticated) {
                const itemToUpdate = cartItems.find(i => i.Product?.id === productId);
                if (!itemToUpdate) return;
                await api.put(`/cart/items/${itemToUpdate.id}`, { quantity });
            } else {
                let localCart = JSON.parse(localStorage.getItem('cart')) || [];
                if (quantity <= 0) {
                    localCart = localCart.filter(i => i.ProductId !== productId);
                } else {
                    const item = localCart.find(i => i.ProductId === productId);
                    if (item) item.quantity = quantity;
                }
                localStorage.setItem('cart', JSON.stringify(localCart));
            }
            await fetchCart();
        } catch (error) {
            console.error("Gagal update jumlah:", error);
            throw error;
        }
    };
    
    const removeFromCart = async (productId) => {
        try {
            if (isAuthenticated) {
                const itemToRemove = cartItems.find(i => i.Product?.id === productId);
                if (!itemToRemove) return;
                await api.delete(`/cart/items/${itemToRemove.id}`);
            } else {
                let localCart = JSON.parse(localStorage.getItem('cart')) || [];
                localCart = localCart.filter(i => i.ProductId !== productId);
                localStorage.setItem('cart', JSON.stringify(localCart));
            }
            await fetchCart();
        } catch(error) {
            console.error("Gagal menghapus item:", error);
            throw error;
        }
    };

    const clearCart = async () => {
        try {
            if (isAuthenticated) {
                await api.delete('/cart');
            } else {
                localStorage.removeItem('cart');
            }
            await fetchCart();
        } catch (error) {
            console.error("Gagal membersihkan keranjang:", error);
            throw error;
        }
    };

    const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);

    return (
        <CartContext.Provider value={{ cartItems, loading, addToCart, updateQuantity, removeFromCart, clearCart, cartCount, fetchCart }}>
            {children}
        </CartContext.Provider>
    );
};